import React, { useState, useEffect, useCallback } from 'react';
import './TutorialOverlay.css';

/**
 * TutorialOverlay Component
 * An interactive step-by-step guide for onboarding users to complex features.
 * 
 * @param {Array} steps - List of steps { targetId, title, content, position }
 * @param {Function} onComplete - Callback when tutorial is finished
 * @param {Function} onClose - Callback when tutorial is dismissed
 */
const TutorialOverlay = ({ steps = [], onComplete, onClose }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [spotlightStyle, setSpotlightStyle] = useState({});
    const [tooltipStyle, setTooltipStyle] = useState({});

    const updateSpotlight = useCallback(() => {
        const step = steps[currentStep];
        if (!step || !step.targetId) {
            setSpotlightStyle({ display: 'none' });
            setTooltipStyle({ display: 'none' });
            return;
        }

        const element = document.getElementById(step.targetId);
        if (!element) {
            setSpotlightStyle({ display: 'none' });
            setTooltipStyle({ display: 'none' });
            return;
        }

        const rect = element.getBoundingClientRect();

        // Don't update if rect is empty (element hidden)
        if (rect.width === 0 && rect.height === 0) return;

        const padding = 8;

        setSpotlightStyle({
            top: rect.top - padding,
            left: rect.left - padding,
            width: rect.width + padding * 2,
            height: rect.height + padding * 2,
            opacity: 1,
            display: 'block'
        });

        // Position tooltip based on target
        const tooltipPadding = 20;
        let tTop = rect.bottom + tooltipPadding;
        let tLeft = rect.left;

        // Basic collision detection for tooltip
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const tooltipWidth = 320;

        if (step.position === 'right') {
            tTop = rect.top;
            tLeft = rect.right + tooltipPadding;
        } else if (step.position === 'left') {
            tTop = rect.top;
            tLeft = rect.left - tooltipWidth - tooltipPadding;
        } else if (step.position === 'top') {
            tTop = rect.top - 200 - tooltipPadding; // 200 is approx height
            tLeft = rect.left;
        }

        // Horizontal bounds check
        if (tLeft + tooltipWidth > windowWidth - 20) {
            tLeft = windowWidth - tooltipWidth - 20;
        }
        if (tLeft < 20) {
            tLeft = 20;
        }

        // Vertical bounds check
        if (tTop + 150 > windowHeight - 20) { // 150 approx min height
            tTop = rect.top - 200 - tooltipPadding;
        }

        setTooltipStyle({
            top: tTop,
            left: tLeft,
            display: 'block'
        });
    }, [currentStep, steps]);

    useEffect(() => {
        const step = steps[currentStep];
        if (step && step.onEnter) {
            step.onEnter();
        }

        // Ensure element is in view
        const element = document.getElementById(step?.targetId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        // Polling for the first 2 seconds to handle animations
        const pollInterval = setInterval(updateSpotlight, 100);
        const pollTimeout = setTimeout(() => clearInterval(pollInterval), 2000);

        window.addEventListener('resize', updateSpotlight);
        window.addEventListener('scroll', updateSpotlight, true); // Catch all scroll events

        return () => {
            clearInterval(pollInterval);
            clearTimeout(pollTimeout);
            window.removeEventListener('resize', updateSpotlight);
            window.removeEventListener('scroll', updateSpotlight, true);
        };
    }, [currentStep, steps, updateSpotlight]);

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            onComplete && onComplete();
        }
    };

    const handlePrev = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    if (steps.length === 0) return null;

    const step = steps[currentStep];

    return (
        <div className="tutorial-overlay">
            <div className="tutorial-backdrop" onClick={onClose} />
            <div className="tutorial-spotlight" style={spotlightStyle} />

            <div className="tutorial-tooltip fade-in" style={tooltipStyle}>
                <div className="tutorial-step-indicator">
                    Step {currentStep + 1} of {steps.length}
                </div>
                <h3>{step.title}</h3>
                <p>{step.content}</p>

                <div className="tutorial-actions">
                    <button className="btn-text" onClick={onClose}>Skip</button>
                    <div className="action-buttons">
                        {currentStep > 0 && (
                            <button className="btn-secondary btn-sm" onClick={handlePrev}>Back</button>
                        )}
                        <button className="btn-primary btn-sm" onClick={handleNext}>
                            {currentStep === steps.length - 1 ? 'Finish' : 'Next'}
                        </button>
                    </div>
                </div>

                <div className="tutorial-progress-bar">
                    <div
                        className="tutorial-progress-fill"
                        style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                    />
                </div>
            </div>
        </div>
    );
};

export default TutorialOverlay;
